/* ==========================================================================
   VEITHDIA · Interacciones del tema
   - <vt-fade-slider>: diapositivas con fundido (hero y destacados)
   - <vt-scroller>: carrusel horizontal con flechas (servicios)
   - Header transparente en portada
   - Animación de aparición y botón "volver arriba"
   ========================================================================== */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Slider con fundido ---------- */
  if (!customElements.get('vt-fade-slider')) {
    class VtFadeSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
        this.delay = parseInt(this.dataset.autoplay || '0', 10);

        this.dots.forEach((dot) =>
          dot.addEventListener('click', () => {
            this.go(parseInt(dot.dataset.dot, 10));
            this.restart();
          })
        );

        this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => { this.go(this.index - 1); this.restart(); }));
        this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => { this.go(this.index + 1); this.restart(); }));

        // Gestos en pantallas táctiles
        let startX = null;
        this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
          startX = null;
        });

        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());

        // Editor de temas: mostrar el bloque seleccionado
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
          if (i > -1) { this.go(i); this.stop(); }
        });
        document.addEventListener('shopify:block:deselect', () => this.start());

        this.start();
      }

      disconnectedCallback() { this.stop(); }

      go(i) {
        if (!this.slides.length) return;
        const n = this.slides.length;
        this.index = (i + n) % n;
        this.slides.forEach((s, k) => {
          const active = k === this.index;
          s.classList.toggle('is-active', active);
          s.setAttribute('aria-hidden', active ? 'false' : 'true');
          s.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', active ? '0' : '-1'));
        });
        this.dots.forEach((d, k) => d.setAttribute('aria-current', k === this.index ? 'true' : 'false'));
      }

      start() {
        if (!this.delay || reduceMotion || this.slides.length < 2 || this.timer) return;
        this.timer = setInterval(() => this.go(this.index + 1), this.delay);
      }

      stop() { clearInterval(this.timer); this.timer = null; }

      restart() { this.stop(); this.start(); }
    }
    customElements.define('vt-fade-slider', VtFadeSlider);
  }

  /* ---------- Carrusel horizontal ---------- */
  if (!customElements.get('vt-scroller')) {
    class VtScroller extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        if (!this.track) return;
        const step = () => {
          const item = this.track.firstElementChild;
          return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : 300;
        };
        this.prev && this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
        this.next && this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }

      update() {
        if (!this.prev || !this.next) return;
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
      }
    }
    customElements.define('vt-scroller', VtScroller);
  }

  /* ---------- Header transparente ---------- */
  const initHeader = () => {
    if (!document.body.classList.contains('vt-header-transparent')) return;
    const header = document.querySelector('.section-header');
    if (!header) return;
    const solid = () => header.classList.add('vt-header--solid');
    const evaluate = () => {
      const open = header.querySelector('details[open]');
      if (window.scrollY > 8 || open || header.matches(':hover') || header.matches(':focus-within')) {
        solid();
      } else {
        header.classList.remove('vt-header--solid');
      }
    };
    window.addEventListener('scroll', evaluate, { passive: true });
    header.addEventListener('mouseenter', evaluate);
    header.addEventListener('mouseleave', () => setTimeout(evaluate, 50));
    header.addEventListener('focusin', evaluate);
    header.addEventListener('focusout', () => setTimeout(evaluate, 50));
    header.addEventListener('toggle', evaluate, true);
    header.addEventListener('click', () => setTimeout(evaluate, 50));
    evaluate();
  };

  /* ---------- Aparición al hacer scroll ---------- */
  const initReveal = () => {
    const items = document.querySelectorAll('.vt-reveal');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Volver arriba ---------- */
  const initTop = () => {
    document.querySelectorAll('[data-vt-top]').forEach((btn) =>
      btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }))
    );
  };

  const init = () => { initHeader(); initReveal(); initTop(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', initReveal);
})();
